import { useEffect, useState, useCallback, useRef } from "react";
import { api, API, BREED_LABELS, errMsg } from "@/lib/api";
import { enqueueMedia, isOnline } from "@/lib/offline";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/shared";
import { toast } from "sonner";
import { Camera, Images, Upload, Trash2, Film, ImageIcon, X, Loader2 } from "lucide-react";

const CHUNK = 5 * 1024 * 1024; // 5MB
const today = () => new Date().toISOString().slice(0, 10);
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export default function Media() {
  const { coops, myCoops, selectedCoop, isOperator } = useApp();
  const subCoopsAll = myCoops.filter((c) => c.type === "coop");
  const locations = coops.filter((c) => c.type === "location" && subCoopsAll.some((s) => s.parent_id === c.id));

  const [locId, setLocId] = useState("");
  const [coopId, setCoopId] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [filterAll, setFilterAll] = useState(false);
  const [queue, setQueue] = useState([]); // {file, url, name, isVideo}
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ i: 0, n: 0, pct: 0 });
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const camRef = useRef(null);
  const galRef = useRef(null);

  const subCoops = subCoopsAll.filter((c) => c.parent_id === locId);
  const selectedSub = subCoopsAll.find((c) => c.id === coopId);
  const token = localStorage.getItem("token");
  const fileUrl = (m) => `${API}/coop-media/${m.id}/file?token=${token}`;

  useEffect(() => {
    const sub = subCoopsAll.find((c) => c.id === selectedCoop) || subCoopsAll[0];
    if (sub) { setLocId(sub.parent_id || ""); setCoopId(sub.id); }
    // eslint-disable-next-line
  }, [selectedCoop, coops]);

  const onLocChange = (id) => {
    setLocId(id);
    setCoopId(subCoopsAll.find((c) => c.parent_id === id)?.id || "");
  };

  const load = useCallback(async () => {
    if (!coopId) { setItems([]); return; }
    const q = `?coop_id=${coopId}${filterAll ? "" : `&date=${date}`}`;
    try {
      const { data } = await api.get(`/coop-media${q}`);
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [coopId, date, filterAll]);
  useEffect(() => { load(); }, [load]);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList).map((file) => ({
      file, name: file.name, isVideo: (file.type || "").startsWith("video"),
      url: URL.createObjectURL(file),
    }));
    setQueue((q) => [...q, ...arr]);
  };

  const removeFromQueue = (idx) => {
    setQueue((q) => { URL.revokeObjectURL(q[idx]?.url); return q.filter((_, i) => i !== idx); });
  };

  const uploadOne = async (item) => {
    const upload_id = uid();
    const total = Math.ceil(item.file.size / CHUNK) || 1;
    for (let i = 0; i < total; i++) {
      const blob = item.file.slice(i * CHUNK, (i + 1) * CHUNK);
      const fd = new FormData();
      fd.append("upload_id", upload_id);
      fd.append("index", String(i));
      fd.append("file", blob, item.name);
      await api.post("/coop-media/chunk", fd);
      setProgress((p) => ({ ...p, pct: Math.round(((i + 1) / total) * 100) }));
    }
    await api.post("/coop-media/complete", {
      upload_id, coop_id: coopId, date, note: note.trim(),
      filename: item.name, content_type: item.file.type || "application/octet-stream",
    });
  };

  const upload = async () => {
    if (!coopId) return toast.error("Lütfen kümes seçin");
    if (!queue.length) return toast.error("Önce fotoğraf veya video seçin");

    // Çevrimdışı: medyayı kuyruğa al, internet gelince yüklensin
    if (!isOnline()) {
      for (const item of queue) {
        await enqueueMedia({
          blob: item.file, filename: item.name,
          content_type: item.file.type || "application/octet-stream",
          coop_id: coopId, date, note: note.trim(), coop_name: selectedSub?.name,
        });
      }
      toast.success(`${queue.length} medya çevrimdışı kaydedildi — internet gelince yüklenecek`);
      queue.forEach((q) => URL.revokeObjectURL(q.url));
      setQueue([]); setNote("");
      return;
    }

    setUploading(true);
    try {
      for (let i = 0; i < queue.length; i++) {
        setProgress({ i: i + 1, n: queue.length, pct: 0 });
        await uploadOne(queue[i]);
      }
      toast.success(`${queue.length} medya yüklendi`);
      queue.forEach((q) => URL.revokeObjectURL(q.url));
      setQueue([]); setNote("");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Yükleme başarısız"));
    } finally {
      setUploading(false);
      setProgress({ i: 0, n: 0, pct: 0 });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/coop-media/${id}`);
      toast.success("Medya silindi");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Kümes Medya" subtitle="Kümes kayıtlarına fotoğraf ve video ekleyin — tabletten çekim yapın veya galeriden seçin." />

      {/* Upload card */}
      <div className="rounded-xl border border-border bg-card overflow-hidden mb-6" data-testid="media-upload-card">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-5 border-b border-border">
          {!isOperator && (
            <Field label="Ana Kümes / Lokasyon">
              <select value={locId} onChange={(e) => onLocChange(e.target.value)} data-testid="media-location"
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">
                <option value="">Seçin</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Alt Kümes">
            <select value={coopId} onChange={(e) => setCoopId(e.target.value)} data-testid="media-coop"
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none">
              <option value="">Seçin</option>
              {subCoops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Tavuk Çeşidi">
            <div className="h-9 flex items-center px-3 rounded-md bg-secondary/60 border border-border text-sm font-medium">
              {selectedSub ? (BREED_LABELS[selectedSub.breed] || "-") : "-"}
            </div>
          </Field>
          <Field label="Tarih">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="media-date"
              className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:ring-2 focus:ring-primary outline-none" />
          </Field>
        </div>

        <div className="p-5 space-y-4">
          <Field label="Not / Açıklama (opsiyonel)">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="media-note"
              placeholder="Örn: yemlik arızası, anormal ölüm, kümes durumu..."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none resize-none" />
          </Field>

          <input ref={camRef} type="file" accept="image/*,video/*" capture="environment" className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="media-camera-input" />
          <input ref={galRef} type="file" accept="image/*,video/*" multiple className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="media-gallery-input" />

          <div className="flex flex-wrap gap-3">
            <button onClick={() => camRef.current?.click()} disabled={uploading} data-testid="media-camera-btn"
              className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
              <Camera size={16} /> Kamera ile Çek
            </button>
            <button onClick={() => galRef.current?.click()} disabled={uploading} data-testid="media-gallery-btn"
              className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-5 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-50">
              <Images size={16} /> Galeriden Seç
            </button>
          </div>

          {queue.length > 0 && (
            <div className="space-y-3" data-testid="media-queue">
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                {queue.map((q, i) => (
                  <div key={i} className="relative group rounded-lg overflow-hidden border border-border bg-secondary/40 aspect-square">
                    {q.isVideo
                      ? <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground"><Film size={22} /><span className="text-[10px] mt-1 px-1 truncate max-w-full">{q.name}</span></div>
                      : <img src={q.url} alt="" className="w-full h-full object-cover" />}
                    {!uploading && (
                      <button onClick={() => removeFromQueue(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`queue-remove-${i}`}>
                        <X size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {uploading && (
                <div data-testid="media-progress">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>Yükleniyor {progress.i}/{progress.n}</span><span>%{progress.pct}</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${progress.pct}%` }} />
                  </div>
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={upload} disabled={uploading} data-testid="media-upload-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50">
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? "Yükleniyor..." : `Yükle (${queue.length})`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Gallery */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-head text-lg font-semibold">
          {selectedSub?.name || "Kümes"} — {filterAll ? "Tüm tarihler" : date}
        </h2>
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="media-filter-all">
          <input type="checkbox" checked={filterAll} onChange={(e) => setFilterAll(e.target.checked)} className="accent-primary" />
          Tüm tarihleri göster
        </label>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center text-sm text-muted-foreground" data-testid="media-empty">
          Bu seçim için medya bulunamadı.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-gallery">
          {items.map((m) => (
            <div key={m.id} className="rounded-xl border border-border bg-card overflow-hidden flex flex-col" data-testid={`media-item-${m.id}`}>
              <div className="relative aspect-video bg-secondary/50 cursor-pointer" onClick={() => setPreview(m)}>
                {m.media_type === "video"
                  ? <video src={fileUrl(m)} className="w-full h-full object-cover" preload="metadata" />
                  : <img src={fileUrl(m)} alt="" className="w-full h-full object-cover" />}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white text-[10px] px-2 py-0.5">
                  {m.media_type === "video" ? <><Film size={11} /> Video</> : <><ImageIcon size={11} /> Foto</>}
                </span>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                {m.note && <p className="text-sm mb-1.5 line-clamp-3">{m.note}</p>}
                <div className="mt-auto flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{m.date} · {m.uploaded_by_name}</span>
                  <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="text-muted-foreground hover:text-destructive transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreview(null)} data-testid="media-preview">
          <button className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center" onClick={() => setPreview(null)}><X size={20} /></button>
          <div className="max-w-4xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
            {preview.media_type === "video"
              ? <video src={fileUrl(preview)} controls autoPlay className="w-full max-h-[75vh] rounded-lg" />
              : <img src={fileUrl(preview)} alt="" className="w-full max-h-[75vh] object-contain rounded-lg" />}
            {preview.note && <p className="text-white/90 text-sm mt-3 text-center">{preview.note}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">{label}</label>
    {children}
  </div>
);
